import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import EmailCommentsPanel from './EmailCommentsPanel';
import CollaborationGuideInfo from './CollaborationGuideInfo';

export interface EmailCommentsDrawerProps {
  open: boolean;
  onClose: () => void;
  emailId: number;
  partitionAt?: string;
  commentCount?: number;
  onCommentAdded?: () => void;
}

const EmailCommentsDrawer: React.FC<EmailCommentsDrawerProps> = ({
  open,
  onClose,
  emailId,
  partitionAt,
  commentCount = 0,
  onCommentAdded,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation('emails');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-950/45 backdrop-blur-[2px]"
        style={zStyle(Z_INDEX.modalBackdrop)}
        onClick={onClose}
        role="presentation"
        aria-hidden
      />
      <aside
        className={cn(
          'fixed top-0 right-0 bottom-0 flex w-full max-w-md flex-col border-l shadow-2xl',
          colors.surface,
          colors.border,
        )}
        style={zStyle(Z_INDEX.modal)}
        role="dialog"
        aria-modal
        aria-labelledby="email-comments-drawer-title"
      >
        <div className={cn('shrink-0 border-b px-4 py-3', colors.border)}>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <h2 id="email-comments-drawer-title" className={cn('text-base font-semibold', colors.text)}>
                  {t('comments')}
                  {commentCount > 0 && (
                    <span className={cn('ml-1.5 text-sm font-normal', colors.textMuted)}>({commentCount})</span>
                  )}
                </h2>
                <CollaborationGuideInfo />
              </div>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{t('commentsMentionHint')}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="shrink-0 rounded-full p-2"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <EmailCommentsPanel
            emailId={emailId}
            partitionAt={partitionAt}
            layout="drawer"
            onCommentAdded={onCommentAdded}
          />
        </div>
      </aside>
    </>
  );
};

export default EmailCommentsDrawer;
